/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Customer, CustomerAccount } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { ESTIMATE_CONSTANTS_CH, MAXIMUM_3A_CONTRIBUTION, RETIREMENT_AGE } from '@app/app.constants';
import { TrackingService } from '@shared/services/tracking.service';
import { StackedAreaChartConfig, StackedAreaChartData } from '@strategy/components';

@Component({
  selector: 'app-strategy-3a-portfolio-estimated-development',
  templateUrl: './strategy-3a-portfolio-estimated-development.component.html',
  styleUrls: ['./strategy-3a-portfolio-estimated-development.component.scss'],
  standalone: false
})
export class Strategy3APortfolioEstimatedDevelopmentComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly windowResize$ = new Subject<string>();
  protected readonly onDestroy$ = new Subject<void>();
  @Input() customer: Customer;
  @ViewChild('estimatedDevelopment3AWrapper', { static: false }) estimatedDevelopment3AWrapper: ElementRef;

  chartData: StackedAreaChartData;
  chartDataInvestedByYear: number[];
  chartConfig: StackedAreaChartConfig;
  currentAccount: CustomerAccount;

  stockSplit: number;
  currentYear = new Date().getFullYear();
  SLIDER_MAX = MAXIMUM_3A_CONTRIBUTION[this.currentYear]['large'];
  SLIDER_MID = MAXIMUM_3A_CONTRIBUTION[this.currentYear]['small'];
  SLIDER_MIN = 100;
  SLIDER_STEP = 10;
  yearly3aInvestment = this.SLIDER_MID;
  sliderInvestment = this.SLIDER_MID;

  showMore = false;
  riskName = 'balanced';
  expectedReturn: number;
  expectedReturnRatio: number;
  expectedPotentialRatio: number;
  expectedLossRatio: number;
  timeToRetirement: number;
  thumbPosition = 50;
  retirementYear: number;
  totalExpectedReturn: number;
  MINIMUM_INVESTMENT: number;

  constructor(
    private store: Store,
    public router: Router,
    private trackingService: TrackingService,
    private cd: ChangeDetectorRef
  ) {
    this.store.pipe(select(fromAppRoot.selectCurrentInvestAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value;
      this.MINIMUM_INVESTMENT = value.minimum_investment;
    });

    this.store.pipe(select(fromStrategy.selectRiskName), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.riskName = value.riskName;
    });

    this.store.pipe(select(fromStrategy.selectCustomerInvestmentData), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.stockSplit = value.stock_split;
      if (this.stockSplit > 0) {
        this.calculateChartData();
      }
    });

    this.store.pipe(select(fromStrategy.select3ARiskQuestionsSorted), takeUntil(this.onDestroy$)).subscribe((questions) => {
      if (!questions || !questions.investmentHorizon[0] || !questions.investmentHorizon[0].survey_customer_answer) {
        return;
      }

      const answerDOB = questions.investmentHorizon[0].survey_customer_answer.answer as string;
      const savedDOB = answerDOB.split('/');
      const difference = this.currentYear - Number(savedDOB[0]);
      this.timeToRetirement = RETIREMENT_AGE - difference;
      this.retirementYear = this.currentYear + this.timeToRetirement;
      this.calculateChartData();
    });

    this.windowResize$.pipe(takeUntil(this.onDestroy$), debounceTime(1000)).subscribe(() => {
      this.calculateChartWidth();
    });
  }

  @HostListener('window:resize', ['$event']) onResize(event: { timeStamp: string }) {
    this.windowResize$.next(event.timeStamp);
  }

  ngOnInit(): void {
    const smallContribution = this.currentAccount.max_contribution_level === 'small';
    if (this.currentAccount.step_status > 2) {
      if (smallContribution) {
        this.SLIDER_MAX = this.SLIDER_MID;
        this.SLIDER_MID = this.SLIDER_MID / 2;
      }
      this.yearly3aInvestment = this.SLIDER_MAX;
      this.sliderInvestment = this.SLIDER_MAX;
      this.thumbPosition = 97;
    }
  }

  ngAfterViewInit(): void {
    this.calculateChartWidth();
  }

  calculateChartData(): void {
    // clear data
    this.chartData = { data: [], columns: ['date', 'badCase', 'expectedCase', 'goodCase'] };

    const total = 100; // default monthly aggregation to start with
    const stocksSplit = this.stockSplit;
    const { stocksReturn, bondsReturn, stocksVol, bondsVol, correlation, confidenceInterval } = ESTIMATE_CONSTANTS_CH;
    const cashReturn = 0;

    const cashRatio = 0.02;
    const actualStockSplit = stocksSplit * (1 - cashRatio);
    const actualBondSplit = 1 - actualStockSplit - cashRatio;

    const expectedReturnRatio = actualStockSplit * stocksReturn + actualBondSplit * bondsReturn + cashRatio * cashReturn;

    const expectedSTDDevRatio = Math.sqrt(
      actualStockSplit ** 2 * stocksVol ** 2 +
        actualBondSplit ** 2 * bondsVol ** 2 +
        cashRatio ** 2 * cashReturn ** 2 +
        2 * actualStockSplit * actualBondSplit * stocksVol * bondsVol * correlation ** 2
    );

    const expectedSTDDevRatioFinal = Math.sqrt(Math.log(1 + expectedSTDDevRatio ** 2 / (1 + expectedReturnRatio) ** 2));
    const expectedReturnRatioFinal = Math.log(1 + expectedReturnRatio) - expectedSTDDevRatioFinal ** 2 / 2;
    const expectedMonthlyReturn = Math.exp(expectedReturnRatioFinal / 12 + (expectedSTDDevRatioFinal / Math.sqrt(12)) ** 2 / 2) - 1;

    this.expectedReturnRatio = expectedReturnRatioFinal;
    this.expectedPotentialRatio = Math.exp(expectedReturnRatioFinal + confidenceInterval * expectedSTDDevRatioFinal) - 1;
    this.expectedLossRatio = Math.exp(expectedReturnRatioFinal - confidenceInterval * expectedSTDDevRatioFinal) - 1;

    let NAV = total;
    let totalInvestedByYear = NAV;
    this.chartDataInvestedByYear = [totalInvestedByYear];

    let WcNAV = 100;
    let BcNAV = 100;
    const currentMonth = new Date().getMonth() + 1;
    let j = 1;
    for (let i = currentMonth; i < this.timeToRetirement * 12 + currentMonth - 1; i++) {
      const returnInPeriod = parseFloat((NAV * expectedMonthlyReturn * 1).toFixed(2));
      let worstCaseReturnBefore = 1;
      if (j > 1) {
        worstCaseReturnBefore = Math.exp(
          (j - 1) * (expectedReturnRatioFinal / 12 - (confidenceInterval * expectedSTDDevRatioFinal) / Math.sqrt(12 * (j - 1)))
        );
      }
      const worstCaseReturn = Math.exp(j * (expectedReturnRatioFinal / 12 - (confidenceInterval * expectedSTDDevRatioFinal) / Math.sqrt(12 * j)));

      WcNAV *= worstCaseReturn / worstCaseReturnBefore;

      let bestCaseReturnBefore = 1;
      if (j > 1) {
        bestCaseReturnBefore =
          Math.exp(expectedReturnRatioFinal / 12 + (confidenceInterval * expectedSTDDevRatioFinal) / Math.sqrt(12 * (j - 1))) ** (j - 1);
      }
      const bestCaseReturn = Math.exp(expectedReturnRatioFinal / 12 + (confidenceInterval * expectedSTDDevRatioFinal) / Math.sqrt(12 * j)) ** j;

      BcNAV *= bestCaseReturn / bestCaseReturnBefore;

      if (i > currentMonth && i % 12 === currentMonth - 1) {
        totalInvestedByYear += this.yearly3aInvestment;
        this.chartDataInvestedByYear.push(totalInvestedByYear);
        NAV += Number(this.yearly3aInvestment) + returnInPeriod;
        WcNAV += this.yearly3aInvestment;
        BcNAV += this.yearly3aInvestment;

        this.chartData.data.push({
          date: new Date(`01/01/${(this.currentYear + i / 12).toFixed()}`),
          badCase: parseFloat(WcNAV.toFixed(0)),
          expectedCase: parseFloat(NAV.toFixed(0)) - parseFloat(WcNAV.toFixed(0)),
          goodCase: parseFloat(BcNAV.toFixed(0)) - parseFloat(NAV.toFixed(0))
        });
      } else {
        this.chartDataInvestedByYear.push(totalInvestedByYear);
        NAV += returnInPeriod;
        this.chartData.data.push({
          date: new Date(`01/01/${(this.currentYear + i / 12).toFixed()}`),
          badCase: parseFloat(WcNAV.toFixed(0)),
          expectedCase: parseFloat(NAV.toFixed(0)) - parseFloat(WcNAV.toFixed(0)),
          goodCase: parseFloat(BcNAV.toFixed(0)) - parseFloat(NAV.toFixed(0))
        });
      }
      j += 1;
    }
    this.totalExpectedReturn = NAV;
  }

  trackActivity(activity: string): void {
    this.trackingService.trackActivity(activity);
  }

  getThumbPosition(): void {
    const left = (this.sliderInvestment / (this.SLIDER_MID * 2 - 16)) * 100;
    if (left < 1) {
      this.thumbPosition = 0;
    } else if (left > 97) {
      this.thumbPosition = 97;
    } else {
      this.thumbPosition = left;
    }

    if (this.currentAccount.step_status <= 2 && this.sliderInvestment > this.SLIDER_MID) {
      const magicConstant = 40.0357;
      const secondHalfRange = (this.sliderInvestment - this.SLIDER_MID) / 10;
      this.yearly3aInvestment = Number(this.SLIDER_MID) + secondHalfRange * magicConstant;
    } else {
      this.yearly3aInvestment = this.sliderInvestment;
    }

    this.calculateChartData();
  }

  toggleShowMore(showMore: boolean) {
    this.showMore = showMore;
    this.trackActivity(`[Button] Estimated assets: Show ${this.showMore ? 'more' : 'less'}`);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private calculateChartWidth(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const containerWidth = this.estimatedDevelopment3AWrapper?.nativeElement?.offsetWidth;

    this.chartConfig = {
      height: 250,
      width: containerWidth ? containerWidth - 40 : 520,
      margin: {
        top: 20,
        bottom: 20,
        left: 15,
        right: 10
      }
    };
    this.cd.detectChanges();
  }
}
